import {
  EmptyState,
  FlexBox,
  SelectionList,
  Spinner,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { ReadSearchApiArg, SearchScope } from 'lib-search-types';
import { HookFormSelectionList } from 'lib-shared-hook-form';
import identity from 'lodash/identity';
import { ComponentProps, useCallback, useMemo, useState } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import { useDebounce, useMount, useUpdateEffect } from 'react-use';
import { Waypoint } from 'react-waypoint';

import { useLazyReadSearchInfiniteQuery } from 'apis/kraken';

import styles from './hookFormSearchSelect.module.scss';
import { HookFormSearchSelectProps } from './hookFormSearchSelect.types';

const defaultGetSuggestions = () => [];

export const HookFormSearchSelect = <
  TScope extends SearchScope,
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  scope,
  initialArg: searchArg,
  lazy = true,
  transformValue = identity,
  renderSelectionListItemContent,
  getSuggestions = defaultGetSuggestions,
  componentProps,
  ...fieldProps
}: HookFormSearchSelectProps<
  TScope,
  TFieldValues,
  TFieldName
>): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);

  const [
    readSearch,
    { results, isFetching, isSuccess, hasNextPage, fetchNextPage },
  ] = useLazyReadSearchInfiniteQuery<TScope>({ infinite: true });

  const baseArg = useMemo(
    (): ReadSearchApiArg<TScope> =>
      ({ scope, ...searchArg }) as ReadSearchApiArg<TScope>,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scope, JSON.stringify(searchArg)],
  );

  useMount(() => {
    if (!lazy) {
      readSearch(baseArg);
    }
  });

  useUpdateEffect(() => {
    readSearch(
      {
        ...baseArg,
        scope,
        query: debouncedSearchTerm || undefined,
      } as ReadSearchApiArg<TScope>,
      { debounceTime: 0 },
    );
  }, [debouncedSearchTerm]);

  const handleToggleDropdown = useCallback(
    (newIsOpen: boolean): void => {
      // If the dropdown is closed, reset the search term
      if (!newIsOpen) {
        setSearchTerm('');
      }

      // If the dropdown is opened and lazy loading is enabled, load the initial list
      if (lazy && newIsOpen && !results.length) {
        readSearch(baseArg);
      }
    },
    [baseArg, lazy, results.length, readSearch],
  );

  const inputSelectionListProps = useMemo(
    (): ComponentProps<
      typeof HookFormSelectionList<TFieldValues[TFieldName]>
    >['componentProps'] => ({
      ...componentProps,
      dropdownProps: {
        ...componentProps?.dropdownProps,
        className: classNames(
          componentProps?.dropdownProps?.className,
          styles.dropdown,
        ),
      },
      filter: {
        name: fieldProps.name,
        value: searchTerm,
        onChange: (e) => setSearchTerm(e.target.value),
      },
      getItemTitleByValue: (value) =>
        componentProps?.getItemTitleByValue?.(value) ?? '',
      onToggleDropdown: handleToggleDropdown,
    }),
    [componentProps, fieldProps.name, handleToggleDropdown, searchTerm],
  );

  const computedResults = useMemo(
    () => [...getSuggestions(debouncedSearchTerm, results), ...results],
    [getSuggestions, debouncedSearchTerm, results],
  );

  return (
    <HookFormSelectionList
      {...fieldProps}
      componentProps={inputSelectionListProps}
    >
      <>
        {computedResults.map((result) => (
          <SelectionList.Item
            key={(result as { id: string }).id}
            value={transformValue(result)}
            className={styles.item}
          >
            {renderSelectionListItemContent?.(result) ??
              (result as { id: string }).id}
          </SelectionList.Item>
        ))}

        {isFetching && (
          <FlexBox as="li" padding="300" justifyContent="center">
            <Spinner size={20} />
          </FlexBox>
        )}

        {isSuccess && !computedResults.length && (
          <FlexBox as="li" padding="300" justifyContent="center">
            <EmptyState
              title="No results found"
              description="Try changing your search criteria."
            />
          </FlexBox>
        )}

        {!isFetching && hasNextPage && <Waypoint onEnter={fetchNextPage} />}
      </>
    </HookFormSelectionList>
  );
};
